/**
 * @fileoverview What follows seed packets being filed, wherever they were scanned from (a
 * bed's card, Add bed, Today's welcome, or a photo of packets in Add photos):
 * the plants added are said in a toast, the garden is read again, and the
 * tips hear a plant was added, so the welcome's first step is finished. On
 * Free, plants left for want of room are said too ("Add the other 3 with
 * Grow More."), and the billing status is read again for the room left.
 *
 * Where: Add photos (PhotoTray,
 * a photo of packets or of several kinds).
 * API: none; it invalidates the plants, beds, Today brief, plant outlook, garden
 * place (gardenWritten), the plant picker's answers (their varieties) and billing status, so each is read again.
 * Filed in several beds at once (a photo of several kinds, each plant to its
 * own bed), the toast says where each went: "Plants added: 1 to Side garden,
 * 1 to Back bed."
 * Words (en/es): packets.doneOne, packets.doneMany, packets.doneSplit,
 * tray.kinds.toBed, packets.joined,
 * billing.free.left, billing.free.leftOne.
 *
 * @module usePacketsFiled
 */

import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useToast } from '@/shared/components/ui/use-toast';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { PacketResult } from '@/shared/api/packets';
import { pickerStale } from '@/shared/components/plant-fields/usePlantPicker';
import { LOCATIONS_QUERY_KEY, PLANTS_QUERY_KEY } from '@/config/tunables/ui';
import { gardenWritten } from '@/shared/lib/briefFreshness';
import { useTips } from '@/features/tips/useTips';
import { BILLING_QUERY_KEY } from '@/features/billing/lib/useBilling';

/**
 * Reads the plants and the beds again, and only those: what a plant or a bed
 * gains when something is filed or a photo is put on or swapped on one (also
 * used by GooglePhotosResume for a Google Photos pick that came late, B40).
 * Fired, not awaited.
 *
 * @param queryClient - The app's client.
 */
export function refreshPlantsAndBeds(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: [PLANTS_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [LOCATIONS_QUERY_KEY] });
}

/**
 * The after-filing steps, as one function to call with what was filed.
 *
 * @returns Says what was added, and reads the garden again.
 */
export function usePacketsFiled(): (result: PacketResult) => void {
  const { happened } = useTips();
  const { reactTranslate: t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  return (result: PacketResult): void => {
    // Plants added: the welcome's first step, and any tip waiting for one, is finished.
    if (result.added > 0) happened('plant_added');
    refreshPlantsAndBeds(queryClient);
    // The brief, the outlook and the garden place (Today's doors end with the first plant, B98): one helper.
    gardenWritten(queryClient);
    // New varieties of theirs, so the next form and scan list them (the plant picker's answers marked stale).
    void pickerStale(queryClient);
    // Free's room for plants changed.
    void queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    const { left } = result;
    const said = [
      result.joined > 0 ? t('packets.joined', { count: result.joined }) : null,
      left > 0 ? (left === 1 ? t('billing.free.leftOne') : t('billing.free.left', { count: left })) : null,
    ].filter(Boolean).join(' ');
    // Several beds: where each went, not one bed's name.
    const split = (result.beds?.length ?? 0) > 1
      ? t('packets.doneSplit', { parts: (result.beds ?? []).map((bed) => t('tray.kinds.toBed', { count: bed.plants, bed: bed.location_name })).join(', ') })
      : null;
    // One bed: its name, which for a photo of several kinds may not be the shared bed when every plant had its own.
    const bedName = result.beds?.length === 1 ? result.beds[0].location_name : result.location_name;
    toast({
      title: split ?? (result.added === 1
        ? t('packets.doneOne', { bed: bedName })
        : t('packets.doneMany', { count: result.added, bed: bedName })),
      description: said || undefined,
    });
  };
}
